import { SyntheticEvent } from 'react';

import { Text } from '@comicrelief/component-library';
import RouteProps from '@/types/shared-types';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getCurrencySymbol } from '@/locale';
import { setComplete, clearErrorMessage, setErrorMessage } from '@/features/paymentProcess/transactionSlice';
import PaymentErrorText from '@/features/paymentProcess/PaymentErrorText';
import Loader from '@/components/loader/Loader';
import SoftHider from '@/components/softHider/SoftHider';
import StepBack from '@/components/stepBack/StepBack';
import PageHeader from '@/components/pageHeader/PageHeader';

import { usePaymentSuccess } from '@/features/paymentProcess/usePaymentSuccess';
import { usePaymentFailure } from '@/features/paymentProcess/usePaymentFailure';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';

import { useGocardlessExecute } from './useGocardlessExecute';
import { billingDayEnding } from './gocardlessPaymentFields';

import {
  StyledButtonWithStates,
  ReviewDetailsWrapper,
  StyledAccordion,
  EditDetailsButton
} from '../../components/global/Global.style';

import {
  TextContainer, StyledText, Detail, ReviewDetailsHeader

} from './GocardlessPayment.style';

import { DirectDebitLogos } from '@/components/providerLogos/ProviderLogos';

function GocardlessConfirmation({ totalSteps, currencySymbol }: RouteProps) {
  const dispatch = useAppDispatch();
  const gocardlessState = useAppSelector(state => state.gocardless);
  const userDetailsState = useAppSelector(state => state.userDetails);
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const journeyNavigate = useJourneyNavigate();

  const paymentSuccess = usePaymentSuccess();
  const paymentFailure = usePaymentFailure();

  const { executeIsLoading, triggerExecuteApi } = useGocardlessExecute();

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    const callExecuteApi = () => {
      void triggerExecuteApi(
        {
          onSuccess: () => {
            dispatch(setComplete(true));
            dispatch(clearErrorMessage());
            paymentSuccess();
          },
          onError: () => {
            dispatch(setErrorMessage('Error processing payment'));
            paymentFailure();
          },
        },
      );
    };

    void callExecuteApi();
  };

  const handleEditDirectDebitDetails = () => {
    // Navigate back to the previous page (user-details)
    journeyNavigate('back');
  };

  return (
    <SoftHider show={!executeIsLoading} loader={<Loader />}>

      <form onSubmit={event => void onSubmit(event)}>

        <PageHeader
          currentStep={4}
          totalSteps={totalSteps}
          headerText="Your Direct Debit"
          initialText="You're making a repeating monthly donation of"
          currencySymbol={currencySymbol}
        />

        <ReviewDetailsHeader>
          Please confirm you would like to set-up a Direct Debit with the following details:
        </ReviewDetailsHeader>

        <PaymentErrorText />

        <ReviewDetailsWrapper data-test="reviewDirectDebitDetails">
          <Detail>
            <Text weight="bold">Account holder name</Text>
            <Text>{gocardlessState.isAccountHolder ? `${userDetailsState.firstName} ${userDetailsState.lastName}` :  gocardlessState.accountHolderName}</Text>
          </Detail>

          <Detail>
            <Text weight="bold">Bank</Text>
            <Text>{gocardlessState.bank}</Text>
          </Detail>

          <Detail>
            <Text weight="bold">Sort code</Text>
            <Text>{gocardlessState.sortCode}</Text>
          </Detail>

          <Detail>
            <Text weight="bold">Account number</Text>
            <Text>{gocardlessState.accountNumber}</Text>
          </Detail>

          <Detail>
            <Text weight="bold">Amount of regular gift</Text>
            <Text>
              {getCurrencySymbol()}
              {paymentSelectState.amount}
            </Text>
          </Detail>

          <Detail>
            <Text weight="bold">Direct Debit billing date</Text>

            <Text>
              {billingDayEnding(Number(gocardlessState.billingDay))}
              {' '}
              of every month
            </Text>
          </Detail>

          <EditDetailsButton
            type="button"
            color="white"
            onClick={handleEditDirectDebitDetails}
            data-test="editDirectDebitDetails"
            fullWidth
          >
            Edit Direct Debit details
          </EditDetailsButton>

        </ReviewDetailsWrapper>

        <TextContainer>
          <StyledText tag="h6" size="xs" color="black" weight="normal">
            By continuing I confirm I am the authorised signatory on my nominated bank or building society account.
          </StyledText>

          <StyledAccordion title="This payment is safeguarded by the Direct Debit Guarantee" textTag="p" weight="700">
            <Text tag="p" size="xs" color="black">
              The Direct Debit guarantee is offered by all Banks and Building Societies that take part in the Direct Debit Scheme.
              The efficiency and security of the scheme is monitored and protected by your own Bank or Building Society.
              If the amounts to be paid or the payment dates change,
              GC re Comic Relief will notify you ten working days in advance of your account being debited or as otherwise agreed.
              If an error is made by GC re Comic Relief or your Bank or Building Society,
              you are guaranteed a full and immediate refund from your branch of the amount paid.
              You can cancel a Direct Debit at any time by writing to your Bank or Building Society.
              Please also send a copy of your letter to us.
            </Text>
          </StyledAccordion>

          <StyledText tag="h6" size="xs" color="black" weight="normal">
            We&apos;ve partnered with the following payment providers to process your donation securely.
          </StyledText>
        </TextContainer>

        <DirectDebitLogos />

        <Detail>
          <StyledButtonWithStates
            type="submit"
            fullWidth
            data-test="gocardlessConfirmDirectDebit"
          >
            Confirm Direct Debit
          </StyledButtonWithStates>
        </Detail>

      </form>

      <StepBack />

    </SoftHider>
  );
}

export default GocardlessConfirmation;
